import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmt, fmtDate } from "../lib/api";
import MapView from "../map/MapView";
import useRideTracking from "../map/useRideTracking";
import { PageTitle, btnGhost, btnPrimary } from "../components/ui";
import { RideDemoBanner, RideHistory, RideStatus, Stars, VEHICLE } from "../lib/ride";

const NEXT = { accepted: ["arrive", "Cheguei à recolha"], arriving: ["start", "Iniciar viagem"], in_progress: ["complete", "Concluir viagem"] };

function CurrentTrip({ id, onChange }) {
  const { trip, mode, refresh } = useRideTracking(id);
  if (!trip) return null;
  const act = async (action) => { try { await api.post(`/ride-demo/driver/trips/${id}/action`, { action }); refresh(); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  const move = async () => { try { await api.post(`/ride-demo/driver/trips/${id}/simulate-move`); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const next = NEXT[trip.status];
  return (
    <section className="bo-card flex flex-col gap-3 p-5" data-testid="driver-current-trip">
      <div className="flex flex-wrap items-center gap-2"><strong>{trip.number}</strong><RideStatus status={trip.status} />
        <span className="ml-auto text-xs text-gray-500" data-testid="driver-tracking-mode">{mode === "live" ? "Em tempo real" : "Atualização periódica"}</span></div>
      <MapView height={260} testid="driver-map" markers={[{ key: "p", pos: trip.pickup, icon: "me", label: "Recolha" },
        { key: "d", pos: trip.destination, icon: "dest", label: "Destino" }, { key: "c", pos: trip.driver_location, icon: "courier", label: "Eu" }]} />
      <p className="text-sm">{trip.estimate.distance_km} km · ~{trip.estimate.duration_min} min · {fmt(trip.estimate.price)} <span className="text-xs text-amber-800">(DEMO, não cobrado)</span></p>
      <div className="flex flex-wrap gap-2">
        <button onClick={move} className={`${btnGhost} px-4 py-1.5`} data-testid="driver-simulate-move-btn">Simular deslocação</button>
        {next && <button onClick={() => act(next[0])} className={`${btnPrimary} px-4 py-1.5`} data-testid={`driver-action-${next[0]}`}>{next[1]}</button>}
        {["accepted", "arriving"].includes(trip.status) && <button onClick={() => act("cancel")} className={`${btnGhost} px-4 py-1.5`} data-testid="driver-action-cancel">Cancelar</button>}
      </div>
      <RideHistory trip={trip} />
    </section>
  );
}

function Requests({ items, reload }) {
  const act = async (id, action) => { try { await api.post(`/ride-demo/driver/trips/${id}/action`, { action }); toast.success(action === "accept" ? "Viagem aceite" : "Pedido recusado"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (!items.length) return <p className="text-sm text-gray-500" data-testid="driver-no-requests">Sem pedidos DEMO de momento.</p>;
  return items.map((t) => (
    <div key={t.id} className="bo-card flex flex-wrap items-center gap-3 p-4 text-sm" data-testid={`driver-request-${t.number}`}>
      <strong>{t.number}</strong><span>{t.estimate.distance_km} km · ~{t.estimate.duration_min} min · {fmt(t.estimate.price)}</span>
      <span className="text-xs text-gray-500">{fmtDate(t.created_at)}</span>
      <span className="ml-auto flex gap-2">
        <button onClick={() => act(t.id, "accept")} className={`${btnPrimary} px-3 py-1.5`} data-testid="driver-accept-btn">Aceitar</button>
        <button onClick={() => act(t.id, "refuse")} className={`${btnGhost} px-3 py-1.5`} data-testid="driver-refuse-btn">Recusar</button>
      </span>
    </div>
  ));
}

export default function DriverDemo() {
  const [me, setMe] = useState(null);
  const [requests, setRequests] = useState([]);
  const [history, setHistory] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/ride-demo/driver/me");
    setMe(data);
    if (!data.driver) return;
    api.get("/ride-demo/driver/trips").then((r) => setHistory(r.data));
    if (data.demo && data.driver.active) api.get("/ride-demo/driver/requests").then((r) => setRequests(r.data)).catch(() => setRequests([]));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  if (!me) return null;
  if (!me.driver) return <p className="bo-card p-5 text-sm text-gray-600" data-testid="driver-no-profile">Esta conta não tem um perfil de motorista DEMO. Os perfis DEMO são criados pela administração.</p>;
  const d = me.driver;
  const toggle = async () => { try { await api.patch("/ride-demo/driver/me/availability", { available: !d.available }); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="flex flex-col gap-4" data-testid="driver-demo-page">
      <PageTitle title="Painel do motorista DEMO" sub={`${d.name} · ${VEHICLE[d.vehicle]}${d.plate ? ` · ${d.plate}` : ""}`} />
      <RideDemoBanner testid="driver-demo-banner" />
      {!me.demo ? <p className="bo-card p-5 text-sm" data-testid="driver-demo-off">O modo DEMO OBÔGO Ride está desativado.</p> : !d.active ? <p className="bo-card p-5 text-sm" data-testid="driver-inactive">Perfil DEMO suspenso pela administração.</p> : (
        <>
          <div className="bo-card flex flex-wrap items-center gap-3 p-4 text-sm">
            <span data-testid="driver-availability">{d.available ? "Disponível para pedidos" : "Indisponível"}</span>
            {d.rating_avg && <span data-testid="driver-rating"><Stars value={Math.round(d.rating_avg)} /> {d.rating_avg} ({d.rating_count})</span>}
            <button onClick={toggle} className={`${btnGhost} ml-auto px-4 py-1.5`} data-testid="driver-availability-toggle">{d.available ? "Ficar indisponível" : "Ficar disponível"}</button>
          </div>
          {me.current ? <CurrentTrip key={me.current.id} id={me.current.id} onChange={load} /> : (
            <section className="flex flex-col gap-2" data-testid="driver-requests"><h2 className="font-bold">Pedidos disponíveis</h2><Requests items={requests} reload={load} /></section>
          )}
        </>
      )}
      <section className="flex flex-col gap-2" data-testid="driver-history"><h2 className="font-bold">Histórico</h2>
        {history.filter((t) => t.id !== me.current?.id).map((t) => (
          <div key={t.id} className="bo-card flex flex-wrap items-center gap-2 p-3 text-sm" data-testid={`driver-history-${t.number}`}>
            <strong>{t.number}</strong><RideStatus status={t.status} /><span className="text-xs text-gray-500">{fmt(t.estimate.price)} · {fmtDate(t.created_at)}</span>
            {t.rating && <Stars value={t.rating.stars} />}
          </div>
        ))}
      </section>
    </div>
  );
}
